import type { EntityReferenceId, ShoppingList, ShoppingTrip, ShoppingTripItem, Store } from "../../app/types.js";
import { localDateOnly } from "../../app/helpers.js";
import { loadShoppingData } from "../../app/loaders.js";
import { ApiError, deleteJson, isTransientNetworkError, postJson } from "../../lib/api.js";
import { EXPECTED_HOUSEHOLD_HEADER, EXPECTED_USER_HEADER, expectedIdentity, OFFLINE_REPLAY_HEADER } from "../../lib/expectedIdentity.js";
import { isBeyondReplayHorizon, newRequestId } from "../../lib/offlineQueue.js";
import { isOptimisticTripItem, recomputeTripTotals } from "./tripCart.js";

// Offline support scoped to shopping: the in-store trip flow must keep working
// with no signal. Successful loads are cached in localStorage; trip-item
// mutations that fail on a network error are queued in an outbox and replayed
// on the next load that reaches the server.

const CACHE_KEY = "household.shopping.cache.v1";
const OUTBOX_KEY = "household.shopping.outbox.v1";

type ShoppingData = { shoppingLists: ShoppingList[]; shoppingTrips: ShoppingTrip[]; stores: Store[] };

export type TripItemPayload = {
  shoppingItemId?: EntityReferenceId | null;
  name: string;
  quantity: number;
  // Optional so a list row with no unit of its own can be sent WITHOUT one and
  // let the API capture the food's unit instead of a synthesized "each" (#935).
  unit?: string;
  priceMinor: number;
  taxable: boolean;
  unitSize?: number | null;
  unitSizeUnit?: string;
  expiresAt?: string;
  locationId?: string | null;
  notes?: string;
  // The local date the shopper made this check-off (#1042). Stamped here rather
  // than by callers because this is the layer that knows a payload may sit in
  // the outbox for hours: replayed without it, the server would anchor the
  // shelf-life suggestion at reconnection time and date Friday's milk from
  // Sunday. Sent on the online path too, where it simply equals the server's
  // own date — one code path, no branch that only runs when the signal drops.
  capturedAt?: string;
};

// `requestId` is the id the FIRST attempt already sent as `Idempotency-Key`
// (#798). Replaying with the same one is what lets the server recognise a write
// whose response was lost and answer it without adding a second cart row.
// Optional so an entry written by an older build still replays (without the
// protection) rather than being dropped.
//
// `attemptedAt` is when the FIRST attempt went out, and it is what bounds the
// replay (codex review). This queue is the normal path for trip-item writes
// now that they are `mustBeOnline`, and it used to retry forever: once the
// server's ledger dropped the row for `requestId`, a replay stopped being
// recognisable as one and was committed as a brand-new write — the duplicate
// this issue is about, just delayed.
//
// It is the SEND time, not the enqueue time, because the server's ledger row
// is created when the attempt reaches it. A request can stall for a long while
// before it fails, and an entry stamped on failure would look that much
// younger than the row it depends on. Optional for entries written before this
// build, which fall back to the timestamp in their id.
//
// `householdId` and `userId` are who the write was made by, and for which
// household (#105), as the generic outbox stores them. The replay names this
// pair rather than whoever the tab belongs to by then, so a write replayed
// after a household switch or a different sign-in shows up as the mismatch it
// is. Optional: entries written before this build have none.
type QueuedUnder = { householdId?: string; userId?: string };

type OutboxEntry = QueuedUnder & (
  | { id: string; kind: "add-trip-item"; tripId: string; payload: TripItemPayload; requestId?: string; attemptedAt?: number }
  // A queued "check off remaining" stays ONE request (codex review). Decomposing
  // it into per-item entries would replay a batch the server may already have
  // committed as writes it has never seen, under ids it cannot recognise — and
  // add every row a second time, which is the exact failure #798 is about.
  | { id: string; kind: "add-trip-items"; tripId: string; payloads: TripItemPayload[]; requestId?: string; attemptedAt?: number }
  | { id: string; kind: "remove-trip-item"; itemId: EntityReferenceId; requestId?: string; attemptedAt?: number }
);

/** The pair a write is being made under, captured with its request id. */
function queuedUnder(): QueuedUnder {
  const { householdId, userId } = expectedIdentity();
  return { ...(householdId ? { householdId } : {}), ...(userId ? { userId } : {}) };
}

/**
 * What a replay says about itself (#105): the pair the write was queued under,
 * and that it is a replay. `api()` keeps headers a caller set, so these win
 * over the tab's current identity. An entry from before this build stored no
 * pair, and sends empty values rather than none, because none would let
 * `api()` fill in the current identity and invent a match. The server reads
 * an empty value as no expectation at all.
 */
function replayHeaders(entry: OutboxEntry): Record<string, string> {
  return {
    [EXPECTED_HOUSEHOLD_HEADER]: entry.householdId ?? "",
    [EXPECTED_USER_HEADER]: entry.userId ?? "",
    [OFFLINE_REPLAY_HEADER]: "1"
  };
}

/** Pending rows of a queued batch are `<entryId>#<index>`; see `pendingItemsFromEntry`. */
const BULK_ROW_SEPARATOR = "#";

/**
 * Trip-item writes are owned by THIS outbox, not the generic one (codex review).
 *
 * Without `mustBeOnline`, `api()` catches the network `TypeError` first — these
 * are queue-safe POSTs — serializes the write into the IndexedDB outbox, and
 * *resolves* with an `__offlineQueued` marker instead of throwing. The `catch`
 * blocks below therefore never ran on the path users actually take, and the
 * marker was handed back as if it were a `ShoppingTripItem`: a malformed cart
 * row for a single add, and a dereferenced `result.trip` for a bulk one.
 *
 * The generic outbox cannot serve this surface anyway. This one rebuilds the
 * pending rows on the next load (`applyOutboxToTrips`), keeps the offline cache
 * in step, degrades a queued batch correctly, and lets a shopper cancel a row
 * that has not synced yet. So the specialised outbox takes the write, and the
 * generic one is told to stay out of it.
 */
const OWNED_BY_SHOPPING_OUTBOX = { mustBeOnline: true } as const;

function isNetworkError(error: unknown) {
  // fetch rejects with TypeError on network failure, but api() also normalizes a
  // network-layer failure to ApiError(status 0) (#278). Treat both as offline so
  // cached reads fall back and trip-item mutations still queue in the outbox.
  return isTransientNetworkError(error);
}

function shouldKeepOutboxEntry(error: unknown): boolean {
  if (isNetworkError(error)) return true;
  if (!(error instanceof ApiError)) return false;
  return error.status === 401 || error.status === 408 || error.status === 429 || error.status >= 500;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or unavailable; offline support degrades gracefully.
  }
}

/**
 * When an entry was queued. Falls back to the timestamp its id already carries
 * (`pending-<ms>-<seq>`), which covers every entry written before `attemptedAt`
 * existed. An id that parses to nothing is left un-aged rather than guessed at:
 * keeping an entry costs a possible duplicate only after 90 days offline,
 * whereas dropping a good one loses a shopper's write outright.
 */
function entryAttemptedAt(entry: OutboxEntry): number | null {
  if (typeof entry.attemptedAt === "number" && Number.isFinite(entry.attemptedAt)) return entry.attemptedAt;
  const stamped = Number(entry.id.split("-")[1]);
  return Number.isFinite(stamped) && stamped > 0 ? stamped : null;
}

function readOutbox(): OutboxEntry[] {
  const stored = readJson<OutboxEntry[]>(OUTBOX_KEY) ?? [];
  const now = Date.now();
  // Expire here rather than in the flush, so a stale entry stops rebuilding a
  // pending row on the trip as well as stopping being replayed (#798).
  const live = stored.filter((entry) => {
    const attemptedAt = entryAttemptedAt(entry);
    return attemptedAt === null || !isBeyondReplayHorizon(attemptedAt, now);
  });
  if (live.length !== stored.length) writeJson(OUTBOX_KEY, live);
  return live;
}

function writeOutbox(entries: OutboxEntry[]) {
  writeJson(OUTBOX_KEY, entries);
}

export function pendingShoppingMutations(): number {
  return readOutbox().length;
}

// Called on sign-out and household switch: cached lists/trips are household
// data and must not survive into another account's session on a shared device.
// Queued offline mutations are dropped too — they belong to the old session's
// household and would only be rejected (404) under a different one.
export function clearShoppingOfflineState(): void {
  try {
    window.localStorage.removeItem(CACHE_KEY);
    window.localStorage.removeItem(OUTBOX_KEY);
  } catch {
    // Storage unavailable; nothing to clear.
  }
}

let pendingSequence = 0;

// Stamp the gesture's own date onto a check-off payload (#1042). Done at the
// moment of the tap — before the request is attempted — so the online and
// queued paths carry the same answer and a payload that ends up in the outbox
// already knows when it was made. A payload that already carries one (a caller
// backdating a correction) keeps it.
function stampCapture(payload: TripItemPayload): TripItemPayload {
  return payload.capturedAt ? payload : { ...payload, capturedAt: localDateOnly() };
}

function pendingItemFromPayload(rowId: string, payload: TripItemPayload, currency: string): ShoppingTripItem {
  const now = new Date().toISOString();
  const entry = { payload };
  return {
    id: rowId as EntityReferenceId,
    shoppingItemId: entry.payload.shoppingItemId ?? null,
    name: entry.payload.name,
    quantity: entry.payload.quantity,
    // The queued payload may deliberately carry no unit; the offline echo shows
    // the generic one until the API replays it and captures the food's own.
    unit: entry.payload.unit ?? "each",
    priceMinor: entry.payload.priceMinor,
    currency,
    taxable: entry.payload.taxable,
    unitSize: entry.payload.unitSize ?? null,
    unitSizeUnit: entry.payload.unitSizeUnit ?? "",
    expiresAt: entry.payload.expiresAt ?? "",
    locationId: entry.payload.locationId ?? null,
    notes: entry.payload.notes ?? "",
    addedAt: now,
    updatedAt: now
  };
}

/**
 * The pending rows one queued entry stands for: a single add is one row, a
 * queued batch is one row per payload, keyed `<entryId>#<index>` so the cart can
 * address them individually while the batch itself stays one request.
 */
function pendingItemsFromEntry(
  entry: Extract<OutboxEntry, { kind: "add-trip-item" | "add-trip-items" }>,
  currency: string
): ShoppingTripItem[] {
  if (entry.kind === "add-trip-item") return [pendingItemFromPayload(entry.id, entry.payload, currency)];
  return entry.payloads.map((payload, index) => pendingItemFromPayload(`${entry.id}${BULK_ROW_SEPARATOR}${index}`, payload, currency));
}

function applyOutboxToTrips(trips: ShoppingTrip[], outbox: OutboxEntry[]): ShoppingTrip[] {
  if (!outbox.length) return trips;
  const removedIds = new Set(outbox.filter((entry) => entry.kind === "remove-trip-item").map((entry) => entry.itemId));
  return trips.map((trip) => {
    const added = outbox
      .filter((entry): entry is Extract<OutboxEntry, { kind: "add-trip-item" | "add-trip-items" }> => (
        (entry.kind === "add-trip-item" || entry.kind === "add-trip-items") && entry.tripId === trip.id
      ))
      .flatMap((entry) => pendingItemsFromEntry(entry, trip.currency));
    const kept = trip.items.filter((item) => !removedIds.has(item.id));
    if (!added.length && kept.length === trip.items.length) return trip;
    return recomputeTripTotals({ ...trip, items: [...kept, ...added] });
  });
}

async function flushShoppingOutbox(): Promise<void> {
  const outbox = readOutbox();
  if (!outbox.length) return;
  const remaining: OutboxEntry[] = [];
  for (const [index, entry] of outbox.entries()) {
    try {
      // Same request id as the attempt that queued this, so a replay of a write
      // the server already committed is answered from its ledger (#798).
      const options = {
        ...OWNED_BY_SHOPPING_OUTBOX,
        ...(entry.requestId ? { idempotencyKey: entry.requestId } : {}),
        headers: replayHeaders(entry)
      };
      if (entry.kind === "add-trip-item") {
        await postJson<ShoppingTripItem>(`/api/v1/shopping-trips/${entry.tripId}/items`, entry.payload, options);
      } else if (entry.kind === "add-trip-items") {
        // Replayed against the BULK endpoint under the id its first attempt
        // sent, so a batch the server already committed is recognised as one
        // request instead of being re-added item by item (codex review).
        await postJson(`/api/v1/shopping-trips/${entry.tripId}/items/bulk`, { items: entry.payloads }, options);
      } else {
        await deleteJson<{ ok: true }>(`/api/v1/shopping-trip-items/${entry.itemId}`, options);
      }
    } catch (error) {
      if (shouldKeepOutboxEntry(error)) {
        // Still offline, unauthenticated, or the server is unavailable: keep
        // this entry and everything after it, preserving operation order.
        remaining.push(...outbox.slice(index));
        break;
      }
      // Server rejected the entry; drop it rather than blocking the queue.
    }
  }
  writeOutbox(remaining);
}

export function isPendingTripItem(item: ShoppingTripItem): boolean {
  return String(item.id).startsWith("pending-");
}

/** `pending-…#2` → the entry it belongs to, and which payload it stands for. */
function splitPendingRowId(rowId: string): [string, number | null] {
  const separator = rowId.lastIndexOf(BULK_ROW_SEPARATOR);
  if (separator < 0) return [rowId, null];
  const index = Number(rowId.slice(separator + 1));
  if (!Number.isInteger(index) || index < 0) return [rowId, null];
  return [rowId.slice(0, separator), index];
}

// The cache holds server truth only. Optimistic placeholders belong to a request
// still in flight, and `pending-` rows are rebuilt from the outbox by
// applyOutboxToTrips on the next load — persisting either would double the row.
function settledItemsOnly(trip: ShoppingTrip): ShoppingTrip {
  const items = trip.items.filter((item) => !isOptimisticTripItem(item) && !isPendingTripItem(item));
  return items.length === trip.items.length ? trip : recomputeTripTotals({ ...trip, items });
}

function sameCartRows(left: ShoppingTrip, right: ShoppingTrip): boolean {
  return left.items.length === right.items.length
    && left.items.every((item, index) => item.id === right.items[index]?.id);
}

// Keep the offline cache in step with an optimistic cart edit (#702). The
// check-off hot path no longer reloads shopping after every tap, and
// loadShoppingDataWithOfflineFallback is the only other writer — so without this
// the cache would still describe the trip as it looked when the trip started. A
// PWA reload on a dropped link would then render a cart the shopper has already
// filled as empty, and they would re-tap rows that are really in it.
export function patchCachedShoppingTrip(tripId: string, update: (trip: ShoppingTrip) => ShoppingTrip): void {
  const cached = readJson<ShoppingData>(CACHE_KEY);
  if (!cached?.shoppingTrips?.length) return;
  let changed = false;
  const shoppingTrips = cached.shoppingTrips.map((trip) => {
    if (trip.id !== tripId) return trip;
    const next = settledItemsOnly(update(trip));
    // An insert-then-strip (a placeholder going in) leaves the cache as it was;
    // only a settled row landing or leaving is worth persisting.
    if (sameCartRows(trip, next)) return trip;
    changed = true;
    return next;
  });
  if (!changed) return;
  writeJson(CACHE_KEY, { ...cached, shoppingTrips });
}

/**
 * `servedFromCache` marks a payload the network could not answer for (#1311).
 * It is still the right thing to render — that is the whole point of the cache —
 * but it says nothing about the present, so a caller deciding whether a record
 * the URL names still exists must not treat it as an answer. Without the flag a
 * deep link to a list created since the cache was written was declared dead
 * against it, and reconnecting could not undo that.
 */
export async function loadShoppingDataWithOfflineFallback(): Promise<ShoppingData & { servedFromCache: boolean }> {
  if (navigator.onLine !== false) await flushShoppingOutbox();
  try {
    const data = await loadShoppingData();
    writeJson(CACHE_KEY, data);
    return { ...data, shoppingTrips: applyOutboxToTrips(data.shoppingTrips, readOutbox()), servedFromCache: false };
  } catch (error) {
    if (!isNetworkError(error)) throw error;
    const cached = readJson<ShoppingData>(CACHE_KEY);
    if (!cached) throw error;
    // A cache written before stores existed (#532) has no stores key.
    return {
      ...cached,
      stores: cached.stores ?? [],
      shoppingTrips: applyOutboxToTrips(cached.shoppingTrips, readOutbox()),
      servedFromCache: true
    };
  }
}

// `currency` lets a queued add hand back the same pending stub a reload would
// rebuild from the outbox, so the caller can show it immediately (#702).
export async function addTripItem(
  tripId: string,
  payload: TripItemPayload,
  currency = "USD"
): Promise<{ item: ShoppingTripItem | null; queued: boolean }> {
  const captured = stampCapture(payload);
  // Minted before the attempt, not when the entry is queued: the id has to be
  // one the server already saw on the request that may have committed (#798).
  // The identity is captured with it, for the same reason (#105).
  const requestId = newRequestId();
  const attemptedAt = Date.now();
  const identity = queuedUnder();
  try {
    const item = await postJson<ShoppingTripItem>(`/api/v1/shopping-trips/${tripId}/items`, captured, { ...OWNED_BY_SHOPPING_OUTBOX, idempotencyKey: requestId });
    return { item, queued: false };
  } catch (error) {
    if (!isNetworkError(error)) throw error;
    const entry: Extract<OutboxEntry, { kind: "add-trip-item" }> = {
      id: `pending-${Date.now()}-${pendingSequence++}`,
      kind: "add-trip-item",
      tripId,
      payload: captured,
      requestId,
      attemptedAt,
      ...identity
    };
    writeOutbox([...readOutbox(), entry]);
    return { item: pendingItemFromPayload(entry.id, entry.payload, currency), queued: true };
  }
}

export type BulkTripItemsResult = {
  items: ShoppingTripItem[];
  skipped: Array<{ name: string; reason: string }>;
  // The server's refreshed trip, or null when the batch was queued offline.
  trip: ShoppingTrip | null;
  queued: boolean;
};

// "Check off remaining" (#702): one request for the whole tail of the list. The
// response carries the refreshed trip, so no follow-up GET is needed. Offline it
// degrades to the same outbox the per-item path uses.
export async function addTripItems(
  tripId: string,
  payloads: TripItemPayload[],
  currency = "USD"
): Promise<BulkTripItemsResult> {
  const captured = payloads.map(stampCapture);
  const bulkRequestId = newRequestId();
  const attemptedAt = Date.now();
  const identity = queuedUnder();
  try {
    const result = await postJson<{
      added: ShoppingTripItem[];
      skipped: Array<{ name: string; reason: string }>;
      trip: ShoppingTrip;
    }>(`/api/v1/shopping-trips/${tripId}/items/bulk`, { items: captured }, { ...OWNED_BY_SHOPPING_OUTBOX, idempotencyKey: bulkRequestId });
    const [trip] = applyOutboxToTrips([result.trip], readOutbox());
    return { items: result.added ?? [], skipped: result.skipped ?? [], trip: trip ?? result.trip, queued: false };
  } catch (error) {
    if (!isNetworkError(error)) throw error;
    // ONE entry carrying the whole batch and the id the attempt already sent.
    // That attempt may have committed, so the replay has to be the same bulk
    // request; splitting it into per-item writes under fresh ids would add every
    // row a second time — the exact failure #798 exists to stop (codex review).
    const entry: Extract<OutboxEntry, { kind: "add-trip-items" }> = {
      id: `pending-${Date.now()}-${pendingSequence++}`,
      kind: "add-trip-items",
      tripId,
      payloads: captured,
      requestId: bulkRequestId,
      attemptedAt,
      ...identity
    };
    writeOutbox([...readOutbox(), entry]);
    return { items: pendingItemsFromEntry(entry, currency), skipped: [], trip: null, queued: true };
  }
}

export async function removeTripItem(item: ShoppingTripItem): Promise<{ queued: boolean }> {
  if (isPendingTripItem(item)) {
    // The add is still queued; cancelling it locally is the whole removal. A row
    // of a queued BATCH drops just that payload and the entry keeps its request
    // id: if the batch did commit, the replay is recognised and answered with
    // the original response, so the row comes back — honestly, because it really
    // was added. If it did not commit, the reduced batch applies. Neither
    // outcome duplicates, which minting a fresh id here would.
    const [entryId, rowIndex] = splitPendingRowId(String(item.id));
    writeOutbox(readOutbox().flatMap((entry) => {
      if (entry.id !== entryId) return [entry];
      if (entry.kind !== "add-trip-items" || rowIndex === null) return [];
      const payloads = entry.payloads.filter((_payload, index) => index !== rowIndex);
      return payloads.length ? [{ ...entry, payloads }] : [];
    }));
    return { queued: false };
  }
  const requestId = newRequestId();
  const attemptedAt = Date.now();
  const identity = queuedUnder();
  try {
    await deleteJson<{ ok: true }>(`/api/v1/shopping-trip-items/${item.id}`, { ...OWNED_BY_SHOPPING_OUTBOX, idempotencyKey: requestId });
    return { queued: false };
  } catch (error) {
    if (!isNetworkError(error)) throw error;
    writeOutbox([
      ...readOutbox(),
      { id: `pending-${Date.now()}-${pendingSequence++}`, kind: "remove-trip-item", itemId: item.id, requestId, attemptedAt, ...identity }
    ]);
    return { queued: true };
  }
}
